import { queryClient } from '@shared/api/queryClient';

/**
 * Browser-held auth state. With no BFF, the SPA talks to the backend directly and keeps the
 * JWT itself (Bearer auth). Only the token lives in localStorage; the user profile is fetched
 * fresh from GET /api/v1/auth/me on every session start (never stale).
 */
const TOKEN_KEY = 'riloma_token';
const DEVICE_KEY = 'riloma_device';

/** Device type sent on login — identifies this client as the web app. */
export const WEB_DEVICE_TYPE = 'WEB' as const;

/** The browser user-agent string, sent on login so the backend can record the device. */
export function getUserAgent(): string {
  return typeof navigator !== 'undefined' ? navigator.userAgent : '';
}

function hasStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

export function getToken(): string | null {
  return hasStorage() ? window.localStorage.getItem(TOKEN_KEY) : null;
}

/** Persist the JWT after a successful login. User profile is fetched from /api/v1/auth/me. */
export function setSession(token: string): void {
  if (!hasStorage()) return;
  window.localStorage.setItem(TOKEN_KEY, token);
}

export function clearSession(): void {
  if (!hasStorage()) return;
  window.localStorage.removeItem(TOKEN_KEY);
  // Migration: remove the legacy riloma_user key that old code wrote on login.
  window.localStorage.removeItem('riloma_user');
}

/**
 * Stable per-browser device id sent on login/logout. The backend keys its device-limit
 * bookkeeping on this, so reusing it keeps the same web "device" slot across logins.
 */
export function getOrCreateDeviceId(): string {
  if (!hasStorage()) return 'web';
  let id = window.localStorage.getItem(DEVICE_KEY);
  if (!id) {
    id = `web-${crypto.randomUUID()}`;
    window.localStorage.setItem(DEVICE_KEY, id);
  }
  return id;
}

/** Purge ALL cached data (PII safety) + the token, then bounce to login. */
export async function hardLogout(): Promise<void> {
  clearSession();
  queryClient.clear();
  if (typeof window !== 'undefined') window.location.assign('/login');
  return Promise.resolve();
}
